'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Building2, BarChart3, Settings, ChevronLeft, ChevronRight, LogOut, Bell, HelpCircle, ExternalLink, Crown,  } from 'lucide-react';

const navItems = [
  {
    group: 'Overview',
    items: [
      { label: 'Analytics', href: '/business-analytics-dashboard', icon: BarChart3, badge: null },
      { label: 'My Listing', href: '/business-listing-management', icon: Building2, badge: null },
    ],
  },
  {
    group: 'Directory',
    items: [
      { label: 'Browse Directory', href: '/search-results-page', icon: LayoutDashboard, badge: null },
      { label: 'My Business Page', href: '/business-detail-page', icon: ExternalLink, badge: null },
    ],
  },
  {
    group: 'Account',
    items: [
      { label: 'Notifications', href: '/business-analytics-dashboard', icon: Bell, badge: '3' },
      { label: 'Settings', href: '/business-listing-management', icon: Settings, badge: null },
      { label: 'Help', href: '/', icon: HelpCircle, badge: null },
    ],
  },
];

export default function DashboardSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className="hidden lg:flex flex-col bg-secondary text-primary-foreground shrink-0 relative"
      style={{ width: collapsed ? 64 : 240, transition: 'width 300ms ease' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10 overflow-hidden">
        <AppLogo size={30} className="shrink-0" />
        {!collapsed && (
          <div className="overflow-hidden">
            <div className="font-bold text-sm leading-tight whitespace-nowrap text-white">West Suffolk</div>
            <div className="text-xs text-purple-300 leading-tight whitespace-nowrap">Business Portal</div>
          </div>
        )}
      </div>
      {/* Featured tier badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded flex items-center gap-2">
          <Crown size={14} className="text-warning shrink-0" />
          <div>
            <div className="text-xs font-bold text-warning">Featured Tier</div>
            <div className="text-xs text-purple-200">Full analytics enabled</div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center mt-3">
          <Crown size={16} className="text-warning" />
        </div>
      )}
      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        {navItems?.map((group) => (
          <div key={`sidebar-group-${group?.group}`} className="mb-4">
            {!collapsed && (
              <div className="section-label px-4 mb-1.5 opacity-50">{group?.group}</div>
            )}
            {group?.items?.map((item) => {
              const isActive = pathname === item?.href;
              return (
                <Link
                  key={`sidebar-item-${item?.label}`}
                  href={item?.href}
                  title={collapsed ? item?.label : undefined}
                  className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded transition-all duration-150 relative group ${
                    isActive
                      ? 'bg-primary text-white' :'text-purple-200 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <item.icon size={18} className="shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-semibold whitespace-nowrap">{item?.label}</span>
                  )}
                  {!collapsed && item?.badge && (
                    <span className="ml-auto bg-negative text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {item?.badge}
                    </span>
                  )}
                  {collapsed && item?.badge && (
                    <span className="absolute top-1 right-1 bg-negative text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {item?.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      {/* User */}
      <div className="border-t border-white/10 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-white">SB</span>
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="text-sm font-semibold truncate text-white">Sutton Builders</div>
              <div className="text-xs text-purple-300 truncate">Featured</div>
            </div>
            <button className="text-purple-300 hover:text-white transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-xs font-bold text-white">SB</span>
            </div>
          </div>
        )}
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 bg-card border border-border rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:bg-muted transition-colors z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} className="text-foreground" /> : <ChevronLeft size={12} className="text-foreground" />}
      </button>
    </aside>
  );
}